import { useState, useEffect, useCallback } from 'react';
import { getTeamData, getPeople, getKidsGroups, getKidsSGLMap, getKidsDailyDetails, saveKidsDailyDetails, getSettings, upsertSetting } from '../../utils/api';
import { BrandSelect, textOn, KIDS_TEAL, normProgram } from './kidsUi';

// Daily Details — Mal's one-click per-block overview: who's serving where
// (auto-built from the block's roster), the small groups with their leaders,
// and staff-editable sections (pre-service / stage game / party / notes) that
// print on an on-brand sheet. Everyone can view + print; only staff edit.

const EMPTY_SECTIONS = { preService: [], stageGame: '', partyActivities: [], meetingNotes: [] };

export default function KidsDailyDetails({ team, showToast, readOnly }) {
  const [blocks, setBlocks] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [peopleById, setPeopleById] = useState({});
  const [groups, setGroups] = useState([]);
  const [sglMap, setSglMap] = useState({});
  const [blockId, setBlockId] = useState('');
  const [sections, setSections] = useState(EMPTY_SECTIONS);
  const [editing, setEditing] = useState(null); // section key in edit mode
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Staff-chosen order for the Team position list (Settings kidsDailyTeamOrder,
  // an array of position labels). Shared across every block since the same
  // position names repeat; unlisted labels fall to the end in roster order.
  const [labelOrder, setLabelOrder] = useState([]);

  useEffect(() => {
    if (!team?.id) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getTeamData(team.id).catch(() => null),
      getPeople().catch(() => []),
      getKidsGroups().catch(() => []),
      getKidsSGLMap().catch(() => ({})),
      getSettings().catch(() => ({})),
    ]).then(([td, people, g, sgl, settings]) => {
      if (cancelled) return;
      // Stored newline-joined (NOT JSON): the deployed Apps Script JSON-parses
      // setting values on write and Sheets then flattens arrays to their first
      // element — a plain multi-line string survives that round-trip intact.
      const rawOrder = settings?.kidsDailyTeamOrder || '';
      if (rawOrder.trim().startsWith('[')) {
        try { setLabelOrder(JSON.parse(rawOrder)); } catch (e) { setLabelOrder([]); }
      } else {
        setLabelOrder(rawOrder.split('\n').map(s => s.trim()).filter(Boolean));
      }
      const bs = (td?.blocks || []).slice().sort((a, b) => {
        if ((a.date || '') !== (b.date || '')) return (a.date || '').localeCompare(b.date || '');
        return (a.startTime || '').localeCompare(b.startTime || '');
      });
      setBlocks(bs);
      setAssignments(td?.assignments || []);
      const map = {};
      (people || []).forEach(p => { map[p.id] = p; });
      setPeopleById(map);
      setGroups(g || []);
      setSglMap(sgl || {});
      // Always land on a block that belongs to THIS team — a blockId left over
      // from a previously-viewed team renders a blank selector and fetches the
      // wrong team's details.
      setBlockId(prev => (prev && bs.some(b => b.id === prev)) ? prev : (bs[0]?.id || null));
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [team?.id]);

  // Load the editable sections whenever the block changes.
  useEffect(() => {
    if (!blockId) return;
    let cancelled = false;
    getKidsDailyDetails(blockId)
      .then(d => { if (!cancelled) setSections(d && typeof d === 'object' ? { ...EMPTY_SECTIONS, ...d } : EMPTY_SECTIONS); })
      .catch(() => { if (!cancelled) setSections(EMPTY_SECTIONS); });
    setEditing(null);
    return () => { cancelled = true; };
  }, [blockId]);

  const block = blocks.find(b => b.id === blockId);
  const blockAssignments = assignments
    .filter(a => a.blockId === blockId && a.status !== 'declined')
    .map(a => ({ ...a, personName: a.personId ? (peopleById[a.personId]?.name || '') : '' }));

  // Group positions by label so "Party Team x5" renders as one row.
  const byLabel = [];
  blockAssignments.forEach(a => {
    const found = byLabel.find(r => r.label === (a.positionLabel || 'Position'));
    if (found) found.people.push(a.personName || '(open)');
    else byLabel.push({ label: a.positionLabel || 'Position', people: [a.personName || '(open)'] });
  });
  // Apply the staff-chosen order (stable: unlisted labels keep roster order, last).
  const orderIdx = l => { const i = labelOrder.indexOf(l); return i === -1 ? 9999 : i; };
  byLabel.sort((a, b) => orderIdx(a.label) - orderIdx(b.label));

  async function moveRow(label, dir) {
    const labels = byLabel.map(r => r.label);
    const i = labels.indexOf(label);
    const j = i + dir;
    if (j < 0 || j >= labels.length) return;
    const other = labels[j];
    // Make sure every visible label exists in the global order (append new
    // ones in their current visible order), then swap just the two involved —
    // orderings made on other blocks stay untouched.
    const next = [...labelOrder];
    labels.forEach(l => { if (!next.includes(l)) next.push(l); });
    const gi = next.indexOf(label);
    const gj = next.indexOf(other);
    [next[gi], next[gj]] = [next[gj], next[gi]];
    setLabelOrder(next);
    try {
      await upsertSetting('kidsDailyTeamOrder', next.join('\n'));
    } catch (e) {
      showToast('Could not save the order — try again.');
    }
  }

  // Small groups + their leaders (from the SGL linkage on the People tab).
  const leadersByGroup = {};
  Object.entries(sglMap).forEach(([personId, groupId]) => {
    if (!leadersByGroup[groupId]) leadersByGroup[groupId] = [];
    const nm = peopleById[personId]?.name;
    if (nm) leadersByGroup[groupId].push(nm);
  });

  // THIS BLOCK's leaders + assistants, matched from the rostered positions.
  // A position label like "5+6 Boys Leader" or "3+4 Girls Assist" is matched
  // to a group by its numbers + gender: '5+6 Boys' → the 5th-6th Boys group,
  // '3s & 4s' (no gender) → the 3s & 4s littles group. Positions that don't
  // name a group (Helicopter, Party Lead, …) simply don't match.
  const groupKey = (text) => {
    const t = String(text || '').toLowerCase();
    const nums = (t.match(/\d+/g) || []).map(Number).sort((a, b) => a - b).join('-');
    if (!nums) return '';
    const gender = /\bboy/.test(t) ? 'b' : /\bgirl/.test(t) ? 'g' : '';
    return nums + '|' + gender;
  };
  const blockLeadersByGroup = {}; // groupId -> { leaders: [], assists: [] }
  const keyToGroupId = {};
  groups.forEach(g => { const k = groupKey(g.name); if (k) keyToGroupId[k] = g.id; });
  blockAssignments.forEach(a => {
    if (!a.personName) return;
    const label = String(a.positionLabel || '');
    const gid = keyToGroupId[groupKey(label)];
    if (!gid) return;
    const isAssist = /assist/i.test(label);
    const isLeader = /lead/i.test(label);
    if (!isAssist && !isLeader) return;
    if (!blockLeadersByGroup[gid]) blockLeadersByGroup[gid] = { leaders: [], assists: [] };
    blockLeadersByGroup[gid][isAssist ? 'assists' : 'leaders'].push(a.personName);
  });

  // Only THIS block's program's groups show: an Elementary service lists the
  // Elementary groups, a 3+4 service the 3s & 4s groups, and so on — inferred
  // from the block title ("THURSDAY PM ELEMENTARY", "FRIDAY AM - 5+6"). A
  // title naming no program (e.g. "TUESDAY SET UP") shows every group.
  const blockProgs = (() => {
    const t = String(block?.title || '').toLowerCase();
    const out = [];
    if (/elem/.test(t)) out.push('Elementary');
    if (t.includes('3') && t.includes('4')) out.push('3-4');
    if (t.includes('5') && t.includes('6')) out.push('5-6');
    return out;
  })();
  const visibleGroups = blockProgs.length
    ? groups.filter(g => blockProgs.includes(normProgram(g.program)))
    : groups;

  const setF = (k, v) => setSections(p => ({ ...p, [k]: v }));

  async function persist(next) {
    setSaving(true);
    try {
      await saveKidsDailyDetails(blockId, next || sections);
    } catch (e) {
      showToast('Save failed — try again.');
    } finally {
      setSaving(false);
    }
  }

  function doneEditing() {
    setEditing(null);
    persist();
    showToast('Saved.');
  }

  function printSheet() {
    const esc = s => String(s).replace(/[&<>]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[ch]));
    const list = arr => arr && arr.length ? '<ul>' + arr.map(x => `<li>${esc(x)}</li>`).join('') + '</ul>' : '<p class="dash">—</p>';
    const blockLabel = block ? `${block.title || 'Block'}${block.day ? ` — ${block.day}` : ''}` : 'Daily Details';
    const chips = visibleGroups.map(g => {
      const c = g.color || KIDS_TEAL, t = textOn(c);
      const sgls = leadersByGroup[g.id] || [];
      const bl = blockLeadersByGroup[g.id] || { leaders: [], assists: [] };
      const part = (label, names) => names.length ? `<div class="gl">${label}</div>${names.map(esc).join('<br>')}` : '';
      const body = part('Leaders', bl.leaders) + part('Assists', bl.assists) + part('SGL', sgls);
      return `<div class="g"><div class="gh" style="background:${c};color:${t}">${esc(g.name)}</div><div class="gb">${body || '<span class="dash">no leader set</span>'}</div></div>`;
    }).join('');
    const hd = `<div class="hd"><div><h1>Daily Details</h1><div class="sub">${esc(blockLabel)}</div></div><div class="org">The Belonging Co<br>Kids Conference 2026</div></div>`;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Daily Details — ${esc(blockLabel)}</title>
      <link href="https://fonts.googleapis.com/css2?family=Archivo:wght@400;700;800;900&family=Lato:wght@300;400;700&display=swap" rel="stylesheet">
      <style>
      @page{margin:0}
      *{box-sizing:border-box}
      body{font-family:'Lato',sans-serif;color:#202020;margin:0;background:#fff}
      .page{padding-bottom:40px}
      .page.brk{page-break-before:always;break-before:page}
      .wrap{padding:0 40px}
      .hd{background:#6AA292;color:#fff;display:flex;justify-content:space-between;align-items:flex-end;padding:20px 40px;margin-bottom:22px}
      .hd h1{font-family:'Archivo';font-weight:900;font-size:24px;margin:0}
      .hd .sub{font-family:'Archivo';font-weight:700;font-size:11px;letter-spacing:.14em;text-transform:uppercase;opacity:.85}
      .hd .org{font-family:'Archivo';font-weight:700;font-size:11px;letter-spacing:.14em;text-transform:uppercase;opacity:.85;text-align:right}
      h2{font-family:'Archivo';font-weight:800;font-size:12px;letter-spacing:.16em;text-transform:uppercase;color:#6AA292;border-bottom:2px solid #E0DED9;padding-bottom:6px;margin:24px 0 12px}
      h2:first-child{margin-top:0}
      table{width:100%;border-collapse:collapse}
      td{padding:7px 8px;border-bottom:1px solid #eee;font-size:14px;vertical-align:top}
      td.r{font-family:'Archivo';font-weight:700;width:170px;color:#202020}
      .cols{display:flex;flex-wrap:wrap;gap:12px}
      .g{border:1px solid #E0DED9;border-radius:10px;overflow:hidden;min-width:150px}
      .gh{font-family:'Archivo';font-weight:800;font-size:12px;letter-spacing:.06em;padding:7px 12px}
      .gb{padding:9px 12px;font-size:14px;line-height:1.5}
      .gl{font-family:'Archivo';font-weight:800;font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:#999;margin-top:5px}
      .gl:first-child{margin-top:0}
      ul{margin:6px 0;padding-left:20px}li{font-size:14px;margin:3px 0}
      .dash{color:#999}
      @media print{.hd,.gh{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
      </style></head><body>
      <div class="page">
        ${hd}
        <div class="wrap">
          <h2>Team</h2>
          ${byLabel.length ? `<table>${byLabel.map(r => `<tr><td class="r">${esc(r.label)}</td><td>${r.people.map(esc).join(', ')}</td></tr>`).join('')}</table>` : '<p class="dash">No one rostered on this block yet.</p>'}
          <h2>Small Groups</h2>
          ${chips ? `<div class="cols">${chips}</div>` : '<p class="dash">No groups yet.</p>'}
        </div>
      </div>
      <div class="page brk">
        ${hd}
        <div class="wrap">
          <h2>Pre-Service Activities</h2>${list(sections.preService)}
          <h2>Stage Game</h2><p${sections.stageGame ? '' : ' class="dash"'}>${esc(sections.stageGame || '—')}</p>
          <h2>Party Activities</h2>${list(sections.partyActivities)}
          <h2>Meeting Notes</h2>${list(sections.meetingNotes)}
        </div>
      </div>
      </body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 400); }
  }

  const PENCIL = <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M8.5 1.5l2 2L3 11H1v-2L8.5 1.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /></svg>;
  const editHeader = (title, k) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <div className="section-header" style={{ marginBottom: 0 }}>{title}</div>
      {!readOnly && (
        <button className="btn-ghost" style={{ padding: '3px 9px', fontSize: 11 }} onClick={() => editing === k ? doneEditing() : setEditing(k)}>
          {editing === k ? 'Done' : PENCIL}
        </button>
      )}
    </div>
  );
  const renderList = (title, k) => (
    <div style={{ marginBottom: 22 }}>
      {editHeader(title, k)}
      {editing === k ? (
        <EditableList items={sections[k]} onChange={v => setF(k, v)} />
      ) : sections[k].length ? (
        <ul style={{ margin: 0, paddingLeft: 20 }}>{sections[k].map((x, i) => <li key={i} style={{ fontFamily: 'var(--font-b)', fontSize: 14, color: 'var(--mid)', marginBottom: 4 }}>{x}</li>)}</ul>
      ) : (
        <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)' }}>—</div>
      )}
    </div>
  );

  const blockLabelFor = b => `${b.title || 'Block'}${b.day ? ` — ${b.day}` : ''}${b.startTime ? ` (${b.startTime})` : ''}`;

  return (
    <div className="page-content">
      <div className="page-title-row" style={{ marginBottom: 16 }}>
        <div>
          <div className="page-title">Daily Details</div>
          <div className="page-sub">One-click overview for each block · who's serving where{readOnly ? ' · view only' : ''}</div>
        </div>
      </div>

      {loading ? (
        <div className="panel" style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>Loading…</div>
      ) : blocks.length === 0 ? (
        <div className="panel" style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>
          No blocks yet — build your session blocks on the roster tabs first, then each one gets a Daily Details page.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 24, flexWrap: 'wrap' }}>
            <BrandSelect
              value={blockId}
              options={blocks.map(b => b.id)}
              labels={Object.fromEntries(blocks.map(b => [b.id, blockLabelFor(b)]))}
              onChange={setBlockId}
            />
            <button className="btn btn-dark" onClick={printSheet}>Print</button>
            {saving && <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)' }}>Saving…</span>}
          </div>

          <div className="two-col" style={{ alignItems: 'start' }}>
            <div className="panel">
              <div className="panel-header"><div className="panel-title">Team</div></div>
              {byLabel.length === 0 ? (
                <div style={{ padding: '20px 24px', fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)' }}>
                  No one rostered on this block yet.
                </div>
              ) : (
                <table className="data-table">
                  <tbody>
                    {byLabel.map((r, i) => (
                      <tr key={r.label} style={{ cursor: 'default' }}>
                        {!readOnly && (
                          <td style={{ width: 26, padding: '4px 0 4px 10px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <button
                                disabled={i === 0}
                                onClick={() => moveRow(r.label, -1)}
                                title="Move up"
                                style={{ border: 'none', background: 'none', cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? 'var(--border)' : 'var(--mid)', padding: 1, lineHeight: 0 }}
                              >
                                <svg width="10" height="7" viewBox="0 0 10 7" fill="none"><path d="M1.5 5.5L5 2l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                              </button>
                              <button
                                disabled={i === byLabel.length - 1}
                                onClick={() => moveRow(r.label, 1)}
                                title="Move down"
                                style={{ border: 'none', background: 'none', cursor: i === byLabel.length - 1 ? 'default' : 'pointer', color: i === byLabel.length - 1 ? 'var(--border)' : 'var(--mid)', padding: 1, lineHeight: 0 }}
                              >
                                <svg width="10" height="7" viewBox="0 0 10 7" fill="none"><path d="M1.5 1.5L5 5l3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                              </button>
                            </div>
                          </td>
                        )}
                        <td className="td-name" style={{ width: 160 }}>{r.label}</td>
                        <td>{r.people.join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div>
              <div className="section-header">Small Groups</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 20 }}>
                {visibleGroups.map(g => {
                  const c = g.color || KIDS_TEAL;
                  const sgls = leadersByGroup[g.id] || [];
                  const bl = blockLeadersByGroup[g.id] || { leaders: [], assists: [] };
                  const roleLabel = (txt) => <span style={{ fontFamily: 'var(--font-h)', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginTop: 6 }}>{txt}</span>;
                  const nameLine = (n) => <div key={n} style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--mid)' }}>{n}</div>;
                  const empty = !sgls.length && !bl.leaders.length && !bl.assists.length;
                  return (
                    <div key={g.id} style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                      <div style={{ background: c, color: textOn(c), fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', padding: '6px 12px' }}>{g.name}</div>
                      <div style={{ padding: '4px 12px 10px' }}>
                        {bl.leaders.length > 0 && <>{roleLabel('Leaders')}{bl.leaders.map(nameLine)}</>}
                        {bl.assists.length > 0 && <>{roleLabel('Assists')}{bl.assists.map(nameLine)}</>}
                        {sgls.length > 0 && <>{roleLabel('SGL')}{sgls.map(nameLine)}</>}
                        {empty && <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--subtle)', display: 'block', marginTop: 6 }}>no leader set</span>}
                      </div>
                    </div>
                  );
                })}
                {visibleGroups.length === 0 && <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)', gridColumn: '1/-1' }}>No small groups yet.</div>}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 8, maxWidth: 640 }}>
            {renderList('Pre-Service Activities', 'preService')}
            <div style={{ marginBottom: 22 }}>
              {editHeader('Stage Game', 'stageGame')}
              {editing === 'stageGame' ? (
                <input className="form-input" value={sections.stageGame} onChange={e => setF('stageGame', e.target.value)} placeholder="e.g. Minute-to-win-it relay" style={{ fontSize: 13, maxWidth: 360 }} onKeyDown={e => e.key === 'Enter' && doneEditing()} />
              ) : (
                <div style={{ fontFamily: 'var(--font-b)', fontSize: 14, color: sections.stageGame ? 'var(--mid)' : 'var(--muted)' }}>{sections.stageGame || '—'}</div>
              )}
            </div>
            {renderList('Party Activities', 'partyActivities')}
            {renderList('Meeting Notes', 'meetingNotes')}
          </div>
        </>
      )}
    </div>
  );
}

function EditableList({ items, onChange }) {
  const [val, setVal] = useState('');
  const addItem = () => { if (val.trim()) { onChange([...items, val.trim()]); setVal(''); } };
  return (
    <div>
      {items.map((it, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <input className="form-input" value={it} onChange={e => { const n = [...items]; n[i] = e.target.value; onChange(n); }} style={{ flex: 1, fontSize: 13 }} />
          <button className="btn-remove" title="Remove" onClick={() => onChange(items.filter((_, j) => j !== i))}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
          </button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6 }}>
        <input className="form-input" placeholder="Add item…" value={val} onChange={e => setVal(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addItem(); }} style={{ flex: 1, fontSize: 13 }} />
        <button className="btn btn-sm btn-dark" onClick={addItem} disabled={!val.trim()}>Add</button>
      </div>
    </div>
  );
}
